import { supabase } from '../lib/supabase';
import { ConsolidacaoPlacaPreview, ConsolidacaoResultado } from '../types/veiculoConsolidacao';
import { HistoricoMotoristaVeiculo } from '../types/veiculoHistorico';
import { veiculoHistoricoMotoristaService, normalizarPlaca } from './veiculoHistoricoMotoristaService';

export const veiculoConsolidacaoService = {
  /**
   * Analisa os veículos e gera uma prévia detalhada de consolidação para cada placa duplicada
   */
  async gerarPreviewConsolidacao(
    veiculos: any[],
    pessoasMap: Map<number, string>,
    marcasMap: Map<number, string>,
    modelosMap: Map<number, string>,
    anosMap: Map<number, string>,
    coresMap: Map<number, string>
  ): Promise<ConsolidacaoPlacaPreview[]> {
    const mapaDuplicadas = veiculoHistoricoMotoristaService.identificarPlacasDuplicadas(veiculos);
    const previews: ConsolidacaoPlacaPreview[] = [];

    for (const [placa, lista] of mapaDuplicadas.entries()) {
      if (lista.length <= 1) continue;

      // Escolhe o veículo principal mais completo (com mais dados preenchidos ou o mais recente ativo)
      const listaOrdenada = [...lista].sort((a, b) => {
        const scoreA = (a.ativo ? 10 : 0) + (a.crlv ? 2 : 0) + (a.renavam ? 2 : 0) + (a.veiculo_modelo_id ? 1 : 0);
        const scoreB = (b.ativo ? 10 : 0) + (b.crlv ? 2 : 0) + (b.renavam ? 2 : 0) + (b.veiculo_modelo_id ? 1 : 0);
        return scoreB - scoreA;
      });

      const principal = listaOrdenada[0];
      const secundarios = listaOrdenada.slice(1);
      const todosIds = listaOrdenada.map(v => String(v.id));

      // Busca todas as OS vinculadas a qualquer um dos IDs desta placa
      let osHistoricoMap = new Map<number, { count: number; minDate: string; maxDate: string }>();
      let totalOS = 0;

      try {
        const { data: osList } = await supabase
          .from('ordem_de_servico')
          .select('id, data_atendimento, agregado, veiculos_id')
          .in('veiculos_id', todosIds);

        if (osList && osList.length > 0) {
          totalOS = osList.length;
          for (const os of osList) {
            const motId = Number(os.agregado);
            if (motId && !isNaN(motId)) {
              const current = osHistoricoMap.get(motId) || { count: 0, minDate: os.data_atendimento || '', maxDate: os.data_atendimento || '' };
              current.count += 1;
              if (os.data_atendimento) {
                if (!current.minDate || os.data_atendimento < current.minDate) current.minDate = os.data_atendimento;
                if (!current.maxDate || os.data_atendimento > current.maxDate) current.maxDate = os.data_atendimento;
              }
              osHistoricoMap.set(motId, current);
            }
          }
        }
      } catch (err) {
        console.warn(`Aviso ao consultar OSs para a placa ${placa}:`, err);
      }

      // Adiciona também os motoristas que estavam configurados nos próprios veículos duplicados
      listaOrdenada.forEach(v => {
        const motId = v.motorista_id || v.agregado_id;
        if (motId && !osHistoricoMap.has(Number(motId))) {
          osHistoricoMap.set(Number(motId), { count: 0, minDate: '', maxDate: '' });
        }
      });

      const motoristasEnvolvidos = Array.from(osHistoricoMap.entries()).map(([motId, stats]) => ({
        motorista_id: motId,
        motorista_nome: pessoasMap.get(motId) || `Motorista #${motId}`,
        totalOS: stats.count,
        primeiraOS: stats.minDate || null,
        ultimaOS: stats.maxDate || null,
      }));

      previews.push({
        placa,
        totalCadastros: lista.length,
        veiculoPrincipal: {
          id: String(principal.id),
          placa: principal.placa,
          motorista_id: principal.motorista_id ? Number(principal.motorista_id) : null,
          motorista_nome: principal.motorista_id ? (pessoasMap.get(Number(principal.motorista_id)) || `Motorista #${principal.motorista_id}`) : 'Sem motorista definido',
          marca_nome: principal.veiculo_marca_id ? marcasMap.get(Number(principal.veiculo_marca_id)) : undefined,
          modelo_nome: principal.veiculo_modelo_id ? modelosMap.get(Number(principal.veiculo_modelo_id)) : undefined,
          ano_nome: principal.veiculo_ano_id ? anosMap.get(Number(principal.veiculo_ano_id)) : undefined,
          cor_nome: principal.veiculo_cor_id ? coresMap.get(Number(principal.veiculo_cor_id)) : undefined,
          renavam: principal.renavam,
          crlv: principal.crlv,
          ativo: principal.ativo !== false,
          status_frota: principal.status_frota,
          km_atual: principal.km_atual,
        },
        veiculosSecundarios: secundarios.map(s => ({
          id: String(s.id),
          placa: s.placa,
          motorista_id: s.motorista_id ? Number(s.motorista_id) : null,
          motorista_nome: s.motorista_id ? (pessoasMap.get(Number(s.motorista_id)) || `Motorista #${s.motorista_id}`) : 'Sem motorista',
        })),
        motoristasEnvolvidos,
        totalOSVinculadas: totalOS,
      });
    }

    return previews;
  },

  /**
   * Executa a unificação de uma placa duplicada:
   * 1. Preserva o histórico de todos os motoristas no serviço de histórico do veículo
   * 2. Migra com segurança todas as Ordens de Serviço (OS) que apontavam para os IDs secundários para o ID principal
   * 3. Garante que cada OS mantenha gravado o seu motorista real (campo agregado)
   * 4. Migra checklists, abastecimentos e manutenções para o ID principal
   * 5. Remove com segurança os registros secundários duplicados da tabela 'veiculos'
   */
  async consolidarPlaca(preview: ConsolidacaoPlacaPreview): Promise<ConsolidacaoResultado> {
    const { placa, veiculoPrincipal, veiculosSecundarios, motoristasEnvolvidos } = preview;
    const placaFormatada = placa.toUpperCase().trim();
    const principalId = veiculoPrincipal.id;
    const idsSecundarios = veiculosSecundarios.map(s => s.id);

    try {
      // 1. REGISTRAR HISTÓRICO COMPLETO DE MOTORISTAS
      const placaNorm = normalizarPlaca(placaFormatada);
      const chaveStorage = `frota_historico_motorista_${placaNorm || placaFormatada}`;
      let historicoMontado: HistoricoMotoristaVeiculo[] = [];

      motoristasEnvolvidos.forEach((mot, idx) => {
        const isAtual = mot.motorista_id === veiculoPrincipal.motorista_id || (idx === 0 && !veiculoPrincipal.motorista_id);
        historicoMontado.push({
          id: `hist_${Date.now()}_${mot.motorista_id || idx}`,
          veiculo_id: principalId,
          placa: placaFormatada,
          motorista_id: mot.motorista_id,
          motorista_nome: mot.motorista_nome,
          data_inicio: mot.primeiraOS || 'Data anterior',
          data_fim: isAtual ? null : (mot.ultimaOS || null),
          motivo: mot.totalOS > 0 ? `Condutor de ${mot.totalOS} ordens de serviço` : 'Registro histórico consolidado',
          is_atual: isAtual,
          total_os: mot.totalOS,
          primeira_os_data: mot.primeiraOS,
          ultima_os_data: mot.ultimaOS,
          created_at: new Date().toISOString(),
        });
      });

      // Ordena histórico
      historicoMontado.sort((a, b) => (b.data_inicio || '').localeCompare(a.data_inicio || ''));

      // Salva histórico
      const jsonStr = JSON.stringify(historicoMontado);
      // system_preference tem apenas as colunas `id` e `preference`.
      const { error: erroPref } = await supabase
        .from('system_preference')
        .upsert({ id: chaveStorage, preference: jsonStr });

      if (erroPref) {
        console.warn('Fallback para localStorage ao salvar preferência:', erroPref.message);
      }
      localStorage.setItem(chaveStorage, jsonStr);

      let osMigradas = 0;

      // 2. MIGRAR ORDENS DE SERVIÇO DOS IDS SECUNDÁRIOS PARA O ID PRINCIPAL
      if (idsSecundarios.length > 0) {
        // Busca as OS dos secundários para garantir que o motorista original esteja preenchido no campo 'agregado'
        const { data: osSecundarias } = await supabase
          .from('ordem_de_servico')
          .select('id, veiculos_id, agregado')
          .in('veiculos_id', idsSecundarios);

        if (osSecundarias && osSecundarias.length > 0) {
          for (const os of osSecundarias) {
            // Descobre o motorista do veículo secundário se a OS não tinha agregado gravado
            let motoristaIdGarantido = os.agregado;
            if (!motoristaIdGarantido) {
              const sec = veiculosSecundarios.find(s => s.id === String(os.veiculos_id));
              if (sec && sec.motorista_id) {
                motoristaIdGarantido = sec.motorista_id;
              }
            }

            const updatePayload: any = {
              veiculos_id: principalId,
            };
            if (motoristaIdGarantido) {
              updatePayload.agregado = motoristaIdGarantido;
            }

            await supabase
              .from('ordem_de_servico')
              .update(updatePayload)
              .eq('id', os.id);
            
            osMigradas += 1;
          }
        }

        // 3. MIGRAR CHECKLISTS & ABASTECIMENTOS (se existirem)
        try {
          await supabase
            .from('veiculo_checklists')
            .update({ veiculo_id: principalId })
            .in('veiculo_id', idsSecundarios);
        } catch (_) {}

        try {
          await supabase
            .from('veiculo_abastecimentos')
            .update({ veiculo_id: principalId })
            .in('veiculo_id', idsSecundarios);
        } catch (_) {}

        // 4. REMOVER COM SEGURANÇA OS REGISTROS SECUNDÁRIOS DA TABELA VEICULOS
        for (const secId of idsSecundarios) {
          try {
            await supabase
              .from('veiculos')
              .delete()
              .eq('id', secId);
          } catch (delErr) {
            console.warn(`Aviso ao excluir veículo secundário ${secId}:`, delErr);
          }
        }
      }

      return {
        placa: placaFormatada,
        veiculoPrincipalId: principalId,
        removidosCount: idsSecundarios.length,
        osMigradasCount: osMigradas,
        historicoRegistradoCount: historicoMontado.length,
        success: true,
      };
    } catch (err: any) {
      console.error(`Erro ao consolidar placa ${placa}:`, err);
      return {
        placa: placaFormatada,
        veiculoPrincipalId: principalId,
        removidosCount: 0,
        osMigradasCount: 0,
        historicoRegistradoCount: 0,
        success: false,
        error: err?.message || 'Falha ao consolidar',
      };
    }
  },

  /**
   * Consolida todas as placas duplicadas encontradas em lote
   */
  async consolidarTodas(previews: ConsolidacaoPlacaPreview[]): Promise<ConsolidacaoResultado[]> {
    const resultados: ConsolidacaoResultado[] = [];
    for (const preview of previews) {
      const res = await this.consolidarPlaca(preview);
      resultados.push(res);
    }
    return resultados;
  }
};
